import { useMemo, useState } from "react";
import Guid from "_shared/guids/guid";
import Proposal from "work/proposals/proposal";

type CommentPaneProposalParams = {
  /** The proposal the page was opened on, which stays identifiable while the envelope reloads. */
  routeProposalId?: Guid;
  proposal?: Proposal | null;
  /** `collaboration.envelope?.negotiationId`, already gated on the canonical mode by the caller. */
  negotiationId?: string;
  /** `collaboration.isLoading` — an envelope is in flight, so its absence is a gap and not an answer. */
  envelopeIsLoading: boolean;
};

/**
 * The proposal the comment layer is keyed on: the page's proposal wearing the negotiation's id.
 *
 * A comment is a Message in a Forum keyed by the durable negotiationId rather than by the
 * per-submission projection id, so every forum, thread and comment-store key has to align on that one
 * id. Both proposal pages derived this identically and inline, and both went on to render the comments
 * pane only while it was present.
 *
 * Which made the pane's whole existence depend on an envelope that is routinely absent for a moment.
 * `collaboration.envelope` is undefined while a refetch, a viewer-scope change or an Ably-driven
 * replacement is in flight — the projection and the negotiation id blink out together, because both are
 * read off that one object — so the pane unmounted and remounted around any of them.
 *
 * The composer inside it seeds its text from the durable draft store once, when it mounts, and writes
 * every keystroke back. Those two facts do not survive an unmount in between: a keystroke that lands
 * across the boundary is written to the store by the instance that is going away, while the instance
 * replacing it has already seeded from the value before it. The author then watches their sentence
 * revert to whatever the composer last held — for a composer whose previous post was refused, that is
 * the refused comment — with no way to tell that what they typed is sitting in the store unread.
 * `Comments` states the rule this breaks: the composer stays mounted so an in-flight refetch "can never
 * unmount the input mid-type and drop the user's comment".
 *
 * So a gap in the envelope is held rather than answered. While one is in flight the pane keeps the
 * identity it already had, which is the identity that same envelope is about to return. A gap that is
 * NOT a reload is still an answer: an envelope that failed, or a page with no proposal at all, gives
 * nothing back and the pane closes exactly as it did before — a viewer whose access was revoked must
 * not be left reading a pane that outlived it. Held per proposal, so arriving at a different one does
 * not show the last one's conversation while its own envelope loads.
 */
export function useCommentPaneProposal(params: CommentPaneProposalParams): Proposal | null | undefined {
  const { routeProposalId, proposal, negotiationId, envelopeIsLoading } = params;

  const negotiationGuid = useMemo(() => {
    if (!negotiationId) return undefined;
    try {
      return new Guid(negotiationId);
    } catch {
      return undefined;
    }
  }, [negotiationId]);

  const keyedProposal = useMemo(
    () =>
      proposal && negotiationGuid && typeof proposal.clone === "function"
        ? proposal.clone({ id: negotiationGuid })
        : proposal,
    [proposal, negotiationGuid],
  );

  const routeKey = routeProposalId?.value;
  const [held, setHeld] = useState<{ routeKey?: string; proposal: Proposal }>();
  // Recorded during render rather than from an effect: the gap can open on the very next render, and a
  // value published a commit later would arrive after the pane it is meant to keep alive had gone. This
  // is React's own "adjust state while rendering" path — it re-renders this hook's owner before anything
  // is shown, and it settles immediately, because the branch is false once `held` carries this proposal.
  if (keyedProposal && held?.proposal !== keyedProposal) {
    setHeld({ routeKey, proposal: keyedProposal });
  }

  if (keyedProposal) return keyedProposal;
  if (!envelopeIsLoading) return keyedProposal;
  return held?.routeKey === routeKey ? held?.proposal : keyedProposal;
}
